import React, { useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";

/**
 * Logo Studio — Pillow-generated wordmark + variants (light/dark/favicon),
 * driven by the site's accent color. Zero AI cost.
 */
export default function LogoStudio({ site, reload }) {
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState(null);
  const [error, setError] = useState("");

  async function generate() {
    setBusy(true); setError(""); setVariants(null);
    try {
      const r = await api.post(`/sites/${site.id}/logo/generate`);
      setVariants(r.data.variants);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  async function apply(v) {
    try {
      await api.post(`/sites/${site.id}/logo/apply`, {
        variant: v.variant,
        logo_url: v.logo_light_url,
        logo_url_dark: v.logo_dark_url,
        favicon_url: v.favicon_url,
      });
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }

  async function clearLogo() {
    if (!window.confirm("Reset to plain text site name in the header?")) return;
    try {
      await api.delete(`/sites/${site.id}/logo`);
      setVariants(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }

  return (
    <div className="space-y-3 w-full">
      {/* Current state */}
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-3 flex items-center gap-3">
        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 min-w-[200px] flex items-center justify-center" style={{ minHeight: 56 }}>
          {site.logo_url_dark ? (
            <img src={site.logo_url_dark} alt={site.name} style={{ height: 32, maxWidth: 280 }} />
          ) : (
            <div className="text-zinc-100 font-semibold text-sm font-display">{site.name}</div>
          )}
        </div>
        <div className="flex-1 text-[11px] text-zinc-500 mono">
          {site.logo_variant ? (
            <>Active variant: <span className="text-zinc-200">{site.logo_variant}</span></>
          ) : (
            "No logo set — header shows plain text site name."
          )}
          {site.favicon_url && (
            <div className="flex items-center gap-2 mt-1">
              <img src={site.favicon_url} alt="favicon" style={{ width: 16, height: 16 }} />
              <span>Favicon ready</span>
            </div>
          )}
        </div>
        <button
          data-testid={SITEDETAIL.logoGenerate}
          onClick={generate}
          disabled={busy}
          className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap"
        >
          {busy ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
          {site.logo_url ? "Regenerate variants" : "Generate logo"}
        </button>
        {site.logo_url && (
          <button
            data-testid={SITEDETAIL.logoClear}
            onClick={clearLogo}
            className="text-xs text-zinc-500 hover:text-red-400 px-2"
            title="Reset to plain text"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>

      {error && <div className="text-xs text-red-400 mono px-1">{error}</div>}

      {/* Variant picker — shown after generation */}
      {variants && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {variants.map(v => (
            <div key={v.variant} className="border border-zinc-800 rounded-sm overflow-hidden" data-testid={`sitedetail-logo-variant-${v.variant}`}>
              <div className="bg-zinc-950 p-4 flex items-center justify-center border-b border-zinc-800" style={{ minHeight: 80 }}>
                <img src={v.logo_dark_url} alt={v.label} style={{ height: 40, maxWidth: "100%" }} />
              </div>
              <div className="bg-zinc-100 p-4 flex items-center justify-center border-b border-zinc-800" style={{ minHeight: 80 }}>
                <img src={v.logo_light_url} alt={v.label} style={{ height: 40, maxWidth: "100%" }} />
              </div>
              <div className="px-3 py-2.5 flex items-center justify-between bg-zinc-900/60">
                <div className="flex items-center gap-2">
                  <img src={v.favicon_url} alt="favicon" style={{ width: 18, height: 18 }} />
                  <div>
                    <div className="text-xs font-display font-semibold text-zinc-100">{v.label}</div>
                    <div className="text-[10px] text-zinc-500 mono">{v.variant}</div>
                  </div>
                </div>
                <button
                  data-testid={SITEDETAIL.logoApply(v.variant)}
                  onClick={() => apply(v)}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-sm ${
                    site.logo_variant === v.variant
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                      : "bg-white text-zinc-950 hover:bg-zinc-200"
                  }`}
                >
                  {site.logo_variant === v.variant ? <><CheckCircle2 size={12} /> In use</> : "Use this"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mono">
        Server-rendered wordmark + auto-favicon. Zero AI cost. Uses your site&apos;s accent color (palette) for the mark. Click Generate to refresh after changing palette.
      </div>
    </div>
  );
}
